/**
 * 👨‍💼 SPACE VENDORS - Module Escadron (Équipe)
 * Gestion des épiciers et livreurs
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Users, Trophy, Star, TrendingUp, Plus,
  Edit, Trash2, ToggleLeft, ToggleRight, Award,
  Loader2, RefreshCw, Crown, Medal, Target
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte vendeur
const VendorCard = ({ vendor, onEdit, onToggle, onDelete, rank }) => {
  const rankColors = {
    1: 'from-yellow-500 to-amber-600 border-yellow-500/50',
    2: 'from-gray-400 to-gray-500 border-gray-400/50',
    3: 'from-amber-600 to-orange-700 border-amber-600/50',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl p-5 border ${
        rank <= 3 && vendor.stamps > 0
          ? `border-2 ${rankColors[rank]?.split(' ')[2] || 'border-orange-500/30'} bg-gradient-to-br ${rankColors[rank]?.split(' ').slice(0, 2).join(' ') || ''}/10`
          : 'border-orange-500/20 bg-[#12121a]/60'
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Avatar avec rang */}
        <div className="relative">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl ${
            rank <= 3 && vendor.stamps > 0
              ? `bg-gradient-to-br ${rankColors[rank]}`
              : 'bg-gradient-to-br from-orange-500 to-amber-600'
          }`}>
            {vendor.name?.charAt(0) || '?'}
          </div>
          {rank <= 3 && vendor.stamps > 0 && (
            <span className="absolute -top-2 -right-2 text-xl">
              {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
            </span>
          )}
        </div>

        {/* Infos */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-bold">{vendor.name}</h3>
            {!vendor.isActive && (
              <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs">Inactif</span>
            )}
          </div>
          <p className="text-gray-500 text-sm">Code: <span className="text-orange-400 font-mono">{vendor.code}</span></p>
        </div>

        {/* Stats */}
        <div className="text-center px-4 border-l border-gray-700">
          <p className="text-2xl font-bold text-orange-400">{vendor.stamps || 0}</p>
          <p className="text-xs text-gray-500">tampons</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggle(vendor)}
            className={`p-2 rounded-lg transition-colors ${
              vendor.isActive 
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
            title={vendor.isActive ? 'Désactiver' : 'Activer'}
          >
            {vendor.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => onEdit(vendor)}
            className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
            title="Modifier"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(vendor)}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            title="Supprimer"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Modal ajout/édition vendeur
const VendorModal = ({ vendor, isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (vendor) {
      setName(vendor.name || '');
      setCode(vendor.code || '');
    } else {
      setName('');
      setCode('');
    }
  }, [vendor]);

  const handleSave = async () => {
    if (!name.trim() || !code.trim()) {
      toast.error('Nom et code requis');
      return;
    }

    setIsLoading(true);
    try {
      if (vendor?.id) {
        await api.updateVendorCode(vendor.id, { name: name.trim(), code: code.trim() });
        toast.success('Épicier mis à jour');
      } else {
        await api.createVendorCode(code.trim(), name.trim());
        toast.success('Épicier créé');
      }
      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border border-orange-500/30">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-400" />
            {vendor ? 'Modifier l\'épicier' : 'Nouvel épicier'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Nom</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Momo, Jean-Phi..."
              className="bg-[#1a1a25] border-orange-500/30 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Code unique</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: MOMO, JEANPHI..."
              className="bg-[#1a1a25] border-orange-500/30 text-white font-mono"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 border-gray-600">
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Module Vendeurs principal
const SpaceVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Charger les vendeurs
  const loadVendors = async () => {
    setIsLoading(true);
    try {
      // Utiliser getVendorCodes pour récupérer la liste
      const data = await api.getVendorCodes() || [];
      // Trier par nombre de tampons
      const sorted = (Array.isArray(data) ? data : data.codes || [])
        .sort((a, b) => (b.stamps || 0) - (a.stamps || 0));
      setVendors(sorted);
    } catch (error) {
      console.error('Erreur chargement vendeurs:', error);
      setVendors([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, []);

  // Toggle actif/inactif
  const handleToggle = async (vendor) => {
    try {
      await api.toggleVendorCode(vendor.id);
      toast.success(vendor.isActive ? 'Épicier désactivé' : 'Épicier activé');
      loadVendors();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Supprimer
  const handleDelete = async (vendor) => {
    if (!window.confirm(`Supprimer ${vendor.name} ?`)) return;
    try {
      await api.deleteVendorCode(vendor.id);
      toast.success('Épicier supprimé');
      loadVendors();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Stats
  const totalStamps = vendors.reduce((sum, v) => sum + (v.stamps || 0), 0);
  const activeVendors = vendors.filter(v => v.isActive).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">
            👨‍💼 ESCADRON
          </h1>
          <p className="text-gray-500 text-sm">Gestion de l'équipe d'épiciers</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={loadVendors}
            variant="outline"
            className="border-orange-500/30 text-orange-400"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={() => { setEditingVendor(null); setShowModal(true); }}
            className="bg-gradient-to-r from-orange-500 to-amber-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel épicier
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-orange-500/20 bg-orange-500/5">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-orange-500/20">
              <Users className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-400">{vendors.length}</p>
              <p className="text-xs text-gray-500">Épiciers</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-green-500/20 bg-green-500/5">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-500/20">
              <Target className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{activeVendors}</p>
              <p className="text-xs text-gray-500">Actifs</p>
            </div>
          </div>
        </div>
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-yellow-500/20 bg-yellow-500/5">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <Trophy className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-400">{totalStamps}</p>
              <p className="text-xs text-gray-500">Tampons total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Battle titre */}
      {vendors.length > 0 && (
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-400" />
            <div>
              <h3 className="text-white font-bold">🏆 BATTLE DES ÉPICIERS</h3>
              <p className="text-gray-400 text-sm">
                En tête : <span className="text-yellow-400 font-bold">{vendors[0]?.name}</span> avec {vendors[0]?.stamps || 0} tampons !
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 backdrop-blur-xl rounded-2xl border border-gray-700 bg-[#12121a]/60">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Aucun épicier</p>
          <p className="text-gray-600 text-sm mt-2">Ajoutez votre premier membre d'équipe</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vendors.map((vendor, idx) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              rank={idx + 1}
              onEdit={() => { setEditingVendor(vendor); setShowModal(true); }}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <VendorModal
        vendor={editingVendor}
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingVendor(null); }}
        onSave={loadVendors}
      />
    </div>
  );
};

export default SpaceVendors;
